import { useEffect, useMemo, useRef, useState } from 'react';
import memberstack from './memberstack_client';
import useMarketIntelligenceStore from '../../Store/marketIntelligenceStore';
import usePlaybackStore from '../../Store/playbackStore';

const AUTH_MODE = process.env.REACT_APP_AUTH_MODE;
const RECONNECT_DELAY_MS = 3000;
const CLIENT_PING_INTERVAL_MS = 30000;

const LOCAL_AUTH = {
  memberId: process.env.REACT_APP_LOCAL_MEMBER_ID,
  planId: process.env.REACT_APP_LOCAL_PLAN_ID,
  status: process.env.REACT_APP_LOCAL_PLAN_STATUS || 'active',
};

function normalizeStatus(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeMessage(rawMessage) {
  try {
    return JSON.parse(rawMessage);
  } catch (error) {
    console.warn('[MIS WSS] Failed to parse message:', rawMessage);
    return null;
  }
}

function getMessageType(message) {
  return message?.type || message?.message_type || message?.event || null;
}

function getMessagePayload(message) {
  return message?.payload || message?.data || message || {};
}

function hasValidCredentials(credentials) {
  return Boolean(
    credentials?.memberId &&
      credentials?.planId &&
      credentials?.status
  );
}

function getManualCredentials() {
  if (!hasValidCredentials(LOCAL_AUTH)) return null;

  return {
    memberId: LOCAL_AUTH.memberId,
    planId: LOCAL_AUTH.planId,
    status: normalizeStatus(LOCAL_AUTH.status),
  };
}

function marketIntelligenceWsUrl() {
  return process.env.REACT_APP_MARKET_INTELLIGENCE_WS_URL;
}

function getCredentialsFromMember(member) {
  const planConnection = member?.planConnections?.find((connection) => (
    connection?.status &&
    (connection?.payment?.priceId || connection?.planId)
  ));

  if (!member?.id || !planConnection) return null;

  return {
    memberId: member.id,
    planId: planConnection.payment?.priceId || planConnection.planId,
    status: normalizeStatus(planConnection.status),
  };
}

async function resolveMemberstackCredentials() {
  if (!memberstack) return null;

  const { data: member } = await memberstack.getCurrentMember();
  return getCredentialsFromMember(member);
}

function buildAuthPayload(credentials) {
  return {
    type: 'authenticate',
    data: {
      memberId: credentials.memberId,
      status: credentials.status,
      planId: credentials.planId,
    },
  };
}

function MarketIntelligenceWebsocketClient({ memberCredentials }) {
  const [browserCredentials, setBrowserCredentials] = useState(null);
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const stopRequestedRef = useRef(false);
  const authFailedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function loadBrowserCredentials() {
      if (AUTH_MODE === 'manual' || hasValidCredentials(memberCredentials)) {
        setBrowserCredentials(null);
        return;
      }

      try {
        const credentials = await resolveMemberstackCredentials();
        if (!cancelled) setBrowserCredentials(credentials);
      } catch (error) {
        console.warn('[MIS WSS] Memberstack auth lookup failed:', error);
        if (!cancelled) {
          useMarketIntelligenceStore
            .getState()
            .setError('Missing MIS WebSocket browser credentials.');
        }
      }
    }

    loadBrowserCredentials();

    return () => {
      cancelled = true;
    };
  }, [
    memberCredentials,
    memberCredentials?.memberId,
    memberCredentials?.planId,
    memberCredentials?.status,
  ]);

  const effectiveCredentials = useMemo(() => {
    if (hasValidCredentials(memberCredentials)) {
      return {
        memberId: memberCredentials.memberId,
        planId: memberCredentials.planId,
        status: normalizeStatus(memberCredentials.status),
      };
    }

    if (AUTH_MODE === 'manual') {
      return getManualCredentials();
    }

    return hasValidCredentials(browserCredentials) ? browserCredentials : null;
  }, [browserCredentials, memberCredentials]);

  useEffect(() => {
    if (!hasValidCredentials(effectiveCredentials)) {
      useMarketIntelligenceStore
        .getState()
        .setError('Missing MIS WebSocket credentials.');

      return undefined;
    }

    const wsUrl = marketIntelligenceWsUrl();
    if (!wsUrl) {
      useMarketIntelligenceStore
        .getState()
        .setError('Missing MIS WebSocket URL.');

      return undefined;
    }

    stopRequestedRef.current = false;
    authFailedRef.current = false;

    function clearReconnectTimer() {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    }

    function clearPingTimer() {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
    }

    function closeSocket() {
      if (!socketRef.current) return;

      try {
        socketRef.current.onopen = null;
        socketRef.current.onmessage = null;
        socketRef.current.onerror = null;
        socketRef.current.onclose = null;

        if (
          socketRef.current.readyState === WebSocket.OPEN ||
          socketRef.current.readyState === WebSocket.CONNECTING
        ) {
          socketRef.current.close();
        }
      } catch (error) {
        console.warn('[MIS WSS] Error closing socket:', error);
      }

      socketRef.current = null;
    }

    function sendPing() {
      const socket = socketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;

      try {
        socket.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
      } catch (error) {
        console.warn('[MIS WSS] Failed to send ping:', error);
      }
    }

    function startPingTimer() {
      clearPingTimer();
      pingTimerRef.current = setInterval(sendPing, CLIENT_PING_INTERVAL_MS);
    }

    function handleMessage(message) {
      const store = useMarketIntelligenceStore.getState();
      const type = getMessageType(message);
      const applySnapshot = (snapshot, meta) => {
        const playback = usePlaybackStore.getState();
        if (playback.mode === 'playing') {
          playback.queueLiveMiSnapshot(snapshot, meta);
          return;
        }
        store.setSnapshot(snapshot, meta);
      };
      const payload = getMessagePayload(message);

      switch (type) {
        case 'auth_success':
          authFailedRef.current = false;
          store.setAuthSuccess();
          return;

        case 'auth_error':
          authFailedRef.current = true;
          store.setAuthError(
            payload?.reason ||
              payload?.message ||
              message?.reason ||
              message?.message ||
              'MIS authentication failed'
          );
          return;

        case 'market_intelligence_initial_snapshot':
          applySnapshot(payload, { updateSource: 'initial_snapshot' });
          return;

        case 'market_intelligence_update':
          applySnapshot(payload, {
            updateSource: payload?.update_source || 'live_update',
            updateReason: payload?.update_reason || null,
          });
          return;

        case 'market_intelligence_snapshot_response':
          applySnapshot(payload, { updateSource: 'snapshot_response' });
          return;

        case 'heartbeat':
        case 'pong':
          store.setHeartbeat(message);
          return;

        case 'ping':
          if (
            socketRef.current &&
            socketRef.current.readyState === WebSocket.OPEN
          ) {
            socketRef.current.send(JSON.stringify({ type: 'pong', ts: Date.now() }));
          }
          return;

        case 'server_error':
          store.setError(
            payload?.message ||
              payload?.reason ||
              message?.message ||
              'MIS server error'
          );
          return;

        default:
          console.log('[MIS WSS] Unhandled message:', message);
      }
    }

    function scheduleReconnect() {
      if (stopRequestedRef.current) return;

      clearReconnectTimer();
      reconnectTimerRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
    }

    function connect() {
      const store = useMarketIntelligenceStore.getState();

      clearReconnectTimer();
      clearPingTimer();
      closeSocket();

      store.setConnectionStatus('connecting');

      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        store.setConnectionStatus('connected');
        socket.send(JSON.stringify(buildAuthPayload(effectiveCredentials)));
        startPingTimer();
      };

      socket.onmessage = (event) => {
        const message = normalizeMessage(event.data);
        if (message) handleMessage(message);
      };

      socket.onerror = () => {
        useMarketIntelligenceStore
          .getState()
          .setError('MIS WebSocket error.');
      };

      socket.onclose = () => {
        clearPingTimer();
        if (authFailedRef.current) return;

        useMarketIntelligenceStore
          .getState()
          .setConnectionStatus('disconnected');

        if (!stopRequestedRef.current) {
          scheduleReconnect();
        }
      };
    }

    connect();

    return () => {
      stopRequestedRef.current = true;

      clearReconnectTimer();
      clearPingTimer();
      closeSocket();

      useMarketIntelligenceStore.getState().setConnectionStatus('closed');
    };
  }, [
    effectiveCredentials,
    effectiveCredentials?.memberId,
    effectiveCredentials?.planId,
    effectiveCredentials?.status,
  ]);

  return null;
}

export default MarketIntelligenceWebsocketClient;
